import React, { FC, useEffect, useRef, useState } from 'react';
import {
  Box,
  Spinner,
  SideSheet,
  SideSheetHeader,
  SideSheetSubHeader,
  SideSheetBody,
  SideSheetSize,
  SideSheetClose,
  SpinnerSize,
} from '@bechtel/gds';
import { t } from '@lingui/macro';
import fetch from 'cross-fetch';
import { ErrorBoundary } from '~/components/atoms';
import { useAppUser, TitleValue } from '~/components/molecules';
import { useEnvProvider } from '~/hooks';

export interface WorkPackage3dViewerProps {
  onClose: () => void;
  workPackageTag: string;
  commodityName?: string;
  commodityTag?: string;
}

function removeSubdirectoryFromUrlString(url) {
  let ssl = false;
  if (url.indexOf('https://') !== -1) {
    ssl = true;
  }

  url = url.replace('http://', '');
  url = url.replace('https://', '');
  const pathArray = url.split('/');
  url = pathArray[0];
  if (ssl) {
    url = 'https://' + url;
  } else {
    url = 'http://' + url;
  }

  return url;
}

export const WorkPackage3dViewer: FC<WorkPackage3dViewerProps> = ({
  onClose,
  workPackageTag,
  commodityTag,
  commodityName,
}) => {
  const { user } = useAppUser();
  const { envConfig } = useEnvProvider();
  const iFrameRef = useRef<HTMLIFrameElement>(null);
  const timerRef = useRef();
  const [isLoaded, setIsLoaded] = useState(false);
  const [token, setToken] = useState('');

  // const envConfig = {
  //   fileUrl: 'https://dev.group01.bechtel.com/model-visualizer/?appId=bps',
  //   authTokenUrl: 'http://localhost:8080/auth-token',
  // };
  const contextId = user?.project?.contextId;
  //Note: In Dev environment BPS ContextId is set as 26173-777-DEV so strip ou the -DEV before sending to iModelViewe
  const projectContextId =
    (contextId.match(/-/g) || []).length > 1 ? contextId.substr(0, contextId.lastIndexOf('-')) : contextId;
  const urlQueryParam =
    commodityTag && commodityName ? `type=tagNumber&ids=${commodityTag}` : `type=workpackage&ids=${workPackageTag}`;
  const iModelUrl = envConfig.fileUrl ? envConfig.fileUrl.concat(`&${urlQueryParam}`) : '';
  //Note: To get //'https://dev.group01.bechtel.com'; from the full URL 'https://dev.group01.bechtel.com/model-visualizer/?appId=bps'
  const iModelHost = removeSubdirectoryFromUrlString(envConfig.fileUrl);

  useEffect(() => {
    const getAuthToken = async () => {
      const resp = await fetch(envConfig.authTokenUrl, {
        credentials: 'include',
      });
      const authToken = await resp.json();
      setToken(authToken.token);
    };
    getAuthToken();
  }, []);

  useEffect(() => {
    if (!token || !isLoaded) return;
    const iFrame = iFrameRef.current;
    if (!iFrame) return;
    const iFrameWindow = iFrame.contentWindow;
    if (!iFrameWindow) return;
    iFrameWindow.postMessage(
      JSON.stringify({
        actionspec: `{"contextIdentifier": "${projectContextId}"}`,
        token: token,
      }),
      iModelHost
    );
    iFrame.src = iFrame.src + '';
  }, [token, isLoaded]);

  useEffect(() => {
    // Clear the interval when the component unmounts
    return () => {
      try {
        if (timerRef && timeRef.current) clearTimeout(timerRef.current);
      } catch (err) {
        console.log(err);
      }
    };
  }, []);

  const handleLoad = () => {
    timerRef.current = setTimeout(() => setIsLoaded(true), 7000);
  };

  return (
    <SideSheet size={SideSheetSize.LARGE} visible onDismiss={onClose}>
      <SideSheetHeader title={t`3D Model`}>
        <SideSheetClose onClick={onClose}></SideSheetClose>
      </SideSheetHeader>
      <SideSheetSubHeader>
        <TitleValue title={t`Work Package`} value={workPackageTag} />
        {commodityTag && commodityName && (
          <Box mt={{ sm: '300' }}>
            <TitleValue title={commodityName} value={commodityTag} />
          </Box>
        )}
      </SideSheetSubHeader>
      <SideSheetBody>
        {!isLoaded && (
          <Spinner size={SpinnerSize.LARGE} style={{ top: '50%', left: '50%', position: 'absolute' }}></Spinner>
        )}
        <ErrorBoundary>
          <Box flexGrow={{ sm: 1 }} style={{ position: 'relative' }}>
            <iframe
              ref={iFrameRef}
              width="100%"
              scrolling="no"
              frameBorder="0"
              src={iModelUrl}
              //src="https://dev.group01.bechtel.com/model-visualizer/?appId=bps&type=tagnumber&ids=02-WL-L001,02-WL-L002,02-WL-L003"
              onLoad={handleLoad}
            ></iframe>
          </Box>
        </ErrorBoundary>
      </SideSheetBody>
    </SideSheet>
  );
};

export default WorkPackage3dViewer;
